'use client';

import { lazy, Suspense, useLayoutEffect, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { SheetTrigger } from '@/components/ui/sheet';
import { Drawer } from '@/shared-ui/molecules/drawer';
import { useProfileDrawerOpenParam } from './hooks/use-profile-drawer-open-param';

const AuthenticatedProfileDrawerContent = lazy(() => import('./authenticated-profile-drawer-content'));
const UnauthenticatedProfileDrawerContent = lazy(() => import('./unauthenticated-profile-drawer-content'));

interface ProfileDrawerProps {
    trigger: ReactElement;
    isAuthenticated?: boolean;
}

export const ProfileDrawer = ({ trigger, isAuthenticated = false }: ProfileDrawerProps): ReactElement => {
    const { t: tHeader } = useTranslation('header');
    const { t } = useTranslation('profileDrawer');
    const [open, setOpen] = useState(false);
    const wasAuthenticatedRef = useRef(isAuthenticated);
    const isLoggingOut = wasAuthenticatedRef.current && !isAuthenticated;

    useProfileDrawerOpenParam({ setOpen });

    useLayoutEffect(() => {
        if (isLoggingOut) {
            setOpen(false);
        }

        wasAuthenticatedRef.current = isAuthenticated;
    }, [isAuthenticated, isLoggingOut]);

    const showDrawerBody = open && !isLoggingOut;

    return (
        <Drawer.Root open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>{trigger}</SheetTrigger>
            <Drawer.Content
                variant="right"
                title={isAuthenticated ? tHeader('menu.yourAccount') : tHeader('signIn')}
                className="w-[calc(100%-48px)] max-w-[290px] border-l border-border bg-snk-cream max-h-dvh">
                <Drawer.CloseButton aria-label={t('common.close')} variant="right" />
                {showDrawerBody ? (
                    <div className="p-0 flex flex-col overflow-x-hidden h-full">
                        <Suspense fallback={null}>
                            {isAuthenticated ? (
                                <AuthenticatedProfileDrawerContent />
                            ) : (
                                <UnauthenticatedProfileDrawerContent setOpen={setOpen} />
                            )}
                        </Suspense>
                    </div>
                ) : null}
            </Drawer.Content>
        </Drawer.Root>
    );
};
